import { useEffect, useState } from "react";
import { Box, Paper } from "@material-ui/core";
import { ThemeProvider } from "@material-ui/styles";
import { defaultMaterialTheme } from "../shared/Style/Style";
import { ContainerWithNavBar } from "../Gamification/shared/ContainerWithNavBar";
import { convertDateAndTimeToLocaleHandler } from "../shared/Utils/ConvertOperations/convertDateAndTimeToLocaleHandler";
import { VersionFormBlock } from "../shared/Versions/VersionFormBlock";
import {
  CreateApplicationVersionDocument,
  CreateApplicationVersionMutation,
  GetApplicationVersionsDocument,
  Os,
  useGetApplicationVersionsQuery,
} from "../../generated/graphql";
import { VersionHistoryBlock } from "../shared/Versions/VersionHistoryBlock";
import { useMutation } from "@apollo/client";
import { useParams } from "react-router-dom";
import { ErrorMessageSnackbar } from "../shared/ErrorMessageSnackbar";

export const InteractiveBoardVersions = () => {
  const { appId } = useParams<{ appId: string }>();
  const [windowsVersion, setWindowsVersion] = useState("");
  const [androidVersion, setAndroidVersion] = useState("");

  const [requestError, setRequestError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const { data, loading } = useGetApplicationVersionsQuery({
    variables: {
      appId,
    },
  });

  const [
    createMobileVersion,
    { data: createMobileVersionData, loading: createMobileVersionLoading },
  ] = useMutation<CreateApplicationVersionMutation>(
    CreateApplicationVersionDocument,
    {
      variables: { appId },
      refetchQueries: [GetApplicationVersionsDocument],
    }
  );

  const disableOnLoading = loading || createMobileVersionLoading;

  const errorHandler = (message: string) => {
    setRequestError(true);
    setErrorMessage(message);
  };

  const createVersionHandle = async (type: Os) => {
    try {
      if (!windowsVersion && !androidVersion) {
        return;
      }

      await createMobileVersion({
        variables: {
          os: type,
          version:
            type === Os.WhiteboardWindows ? windowsVersion : androidVersion,
        },
      });
    } catch (e) {
      errorHandler("При збереженні версії виникла помилка");
    }
  };

  const versionsData = data?.getApplicationVersions ?? [];
  const versionsWindows = versionsData.filter(
    (version) => version.os === Os.WhiteboardWindows
  );
  const versionsAndroid = versionsData.filter(
    (version) => version.os === Os.WhiteboardAndroid
  );

  useEffect(() => {
    if (
      createMobileVersionData?.createApplicationVersion.os ===
      Os.WhiteboardWindows
    ) {
      setWindowsVersion("");
    }
    if (
      createMobileVersionData?.createApplicationVersion.os ===
      Os.WhiteboardAndroid
    ) {
      setAndroidVersion("");
    }
  }, [createMobileVersionData]);

  return (
    <>
      <ContainerWithNavBar header="Версії застосунку для дошки">
        <Paper elevation={4}>
          <ThemeProvider theme={defaultMaterialTheme}>
            <Box p="60px 0 120px 0">
              <Box display="flex" justifyContent="space-around" flexWrap="wrap">
                <VersionFormBlock
                  title="Версія для Windows"
                  value={windowsVersion}
                  onChange={({ target: { value } }) => setWindowsVersion(value)}
                  onSave={() => createVersionHandle(Os.WhiteboardWindows)}
                  disabled={disableOnLoading}
                  lastVersion={versionsWindows[0]?.version || ""}
                  lastDate={
                    convertDateAndTimeToLocaleHandler(
                      versionsWindows[0]?.createdDate
                    ) || ""
                  }
                />

                <VersionFormBlock
                  title="Версії для Android"
                  value={androidVersion}
                  onChange={({ target: { value } }) => setAndroidVersion(value)}
                  onSave={() => createVersionHandle(Os.WhiteboardAndroid)}
                  disabled={disableOnLoading}
                  lastVersion={versionsAndroid[0]?.version || ""}
                  lastDate={
                    convertDateAndTimeToLocaleHandler(
                      versionsAndroid[0]?.createdDate
                    ) || ""
                  }
                />
              </Box>
              <Box display="flex" justifyContent="center">
                <h2>Історія оновлень дошок</h2>
              </Box>
              <Box display="flex" justifyContent="space-around">
                <VersionHistoryBlock versions={versionsWindows} />
                <VersionHistoryBlock versions={versionsAndroid} />
              </Box>
            </Box>
          </ThemeProvider>
        </Paper>
      </ContainerWithNavBar>
      {requestError && (
        <ErrorMessageSnackbar
          open={requestError}
          closeHandler={() => setRequestError(false)}
          message={errorMessage}
        />
      )}
    </>
  );
};
